import { processImageBatch } from './imageProcessing';
import { isHeicFile, convertHeicToJpeg } from './imageUtils';
import type { ProcessingProgress } from './imageProcessing';

function alignFilesWithBatchResults(
  files: File[],
  batchResults: Array<{ original: string; thumbnail: string } | null>
): { files: File[]; originals: string[]; thumbnails: string[] } {
  const alignedFiles: File[] = [];
  const originals: string[] = [];
  const thumbnails: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const result = batchResults[i];
    if (result) {
      alignedFiles.push(files[i]);
      originals.push(result.original);
      thumbnails.push(result.thumbnail);
    }
  }

  return { files: alignedFiles, originals, thumbnails };
}

export interface ImageGroup {
  prefix: string;
  files: File[];
  originals: string[];
  thumbnails: string[];
}

export interface ProcessedImageSet {
  files: File[];
  originals: string[];
  thumbnails: string[];
}

/**
 * - picker: preserve OS / gallery selection order (新しい順・選択順)
 * - shooting: reverse for chronological shooting order (古い順・撮影順)
 */
export type PhotoImportOrder = 'picker' | 'shooting';

export function isMobileImportDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export function getDefaultPhotoImportOrder(): PhotoImportOrder {
  return isMobileImportDevice() ? 'shooting' : 'picker';
}

export function applyPhotoImportOrder(
  images: ProcessedImageSet,
  order: PhotoImportOrder
): ProcessedImageSet {
  if (order === 'picker') {
    return images;
  }

  return {
    files: [...images.files].reverse(),
    originals: [...images.originals].reverse(),
    thumbnails: [...images.thumbnails].reverse(),
  };
}

export async function processImageGroups(
  files: FileList,
  onProgress?: (progress: ProcessingProgress) => void
): Promise<ImageGroup[]> {
  // Preserve gallery / file-picker selection order (critical on mobile)
  const orderedFiles = Array.from(files).filter(
    file => file.type.startsWith('image/') || isHeicFile(file)
  );

  // Convert HEIC files in parallel (multi-threaded style)
  const convertPromises = orderedFiles.map(async (file, i) => {
    if (onProgress) {
      onProgress({
        current: i,
        total: orderedFiles.length,
        phase: isHeicFile(file) ? 'wasm-converting' : 'converting'
      });
    }
    try {
      if (isHeicFile(file)) {
        console.log(`🔄 WebAssembly HEIC変換: ${file.name} を JPEG に変換中...`);
        const convertedFile = await convertHeicToJpeg(file);
        console.log(`✅ WebAssembly HEIC変換完了: ${file.name}`);
        return convertedFile;
      } else {
        return file;
      }
    } catch (error) {
      console.error(`❌ WebAssembly HEIC変換失敗: ${file.name}`, error);
      return file; // Fall back to original file
    }
  });

  const convertedFiles: File[] = await Promise.all(convertPromises);

  // Update progress to indicate HEIC conversion completion
  if (onProgress) {
    onProgress({
      current: orderedFiles.length,
      total: orderedFiles.length,
      phase: 'converting'
    });
  }

  const batchResults = await processImageBatch(convertedFiles, onProgress);
  const aligned = alignFilesWithBatchResults(convertedFiles, batchResults);

  return [{
    prefix: 'group',
    files: aligned.files,
    originals: aligned.originals,
    thumbnails: aligned.thumbnails
  }];
}

export function getInitialGroupSizes(totalImages: number, defaultSize: number): { [key: string]: number } {
  const sizes: { [key: string]: number } = {};
  let remainingImages = totalImages;
  let currentIndex = 0;

  while (remainingImages > 0) {
    const size = Math.min(10, Math.min(defaultSize, remainingImages));
    sizes[currentIndex.toString()] = size;
    remainingImages -= size;
    currentIndex++;
  }

  return sizes;
}

export function createImageGroups(
  files: File[],
  originals: string[],
  thumbnails: string[],
  groupSizes: { [key: string]: number }
): ImageGroup[] {
  const groups: ImageGroup[] = [];
  let currentIndex = 0;

  const sortedEntries = Object.entries(groupSizes)
    .sort(([a], [b]) => Number(a) - Number(b));

  for (const [_, size] of sortedEntries) {
    if (currentIndex < files.length) {
      const groupSize = Math.min(10, size);
      const groupFiles = files.slice(currentIndex, currentIndex + groupSize);
      const groupOriginals = originals.slice(currentIndex, currentIndex + groupSize);
      const groupThumbnails = thumbnails.slice(currentIndex, currentIndex + groupSize);

      if (groupFiles.length > 0) {
        groups.push({
          prefix: `group${groups.length}`,
          files: groupFiles,
          originals: groupOriginals,
          thumbnails: groupThumbnails
        });
      }

      currentIndex += groupSize;
    }
  }

  return groups;
}

export function updateGroupSize(
  allFiles: File[],
  allOriginals: string[],
  allThumbnails: string[],
  groupSizes: { [key: string]: number },
  groupIndex: number,
  newSize: number
): { updatedSizes: { [key: string]: number }; updatedGroups: ImageGroup[] } {
  const validatedSize = Math.max(1, Math.min(10, Math.min(newSize, allFiles.length)));
  const updatedSizes = { ...groupSizes };
  updatedSizes[groupIndex] = validatedSize;

  const updatedGroups = createImageGroups(
    allFiles,
    allOriginals,
    allThumbnails,
    updatedSizes
  );

  return {
    updatedSizes,
    updatedGroups
  };
}

export function reorderImages(group: ImageGroup, fromIndex: number, toIndex: number): ImageGroup {
  const moveItem = <T>(arr: T[], from: number, to: number): T[] => {
    const newArr = [...arr];
    const [item] = newArr.splice(from, 1);
    newArr.splice(to, 0, item);
    return newArr;
  };

  return {
    ...group,
    files: moveItem(group.files, fromIndex, toIndex),
    originals: moveItem(group.originals, fromIndex, toIndex),
    thumbnails: moveItem(group.thumbnails, fromIndex, toIndex)
  };
}

export async function processGroupFiles(files: File[]): Promise<{ originals: string[]; thumbnails: string[] }> {
  const batchResults = await processImageBatch(files);
  const aligned = alignFilesWithBatchResults(files, batchResults);
  return {
    originals: aligned.originals,
    thumbnails: aligned.thumbnails
  };
}